import React, { useCallback, useMemo, useState } from 'react';
import { FormControl, FormLabel, MenuItem, TextField } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import MotionVotingControls from '../../../MotionVotingControls';
import { Motion } from 'store/types/Meeting';
import { defaultTinyMCEProps } from 'util/form';
import {
  createImagesUploadHandler,
  excludeAttendee,
  getAttendeeOptions,
  useSafeAttendeeDisplayValue,
} from 'util/meeting';

const defaultStatement = 'To incorporate the technical change(s).';

const IncorporateTechnicalChangeMotion: React.FunctionComponent = () => {
  const { meetingId, attendees, meetingSession } = useMeetingHubContext();
  const [statement, setStatement] = useState(defaultStatement);
  const [by, setBy] = useState('');
  const [second, setSecond] = useState('');
  const [discussion, setDiscussion] = useState('');

  const byAttendees = useMemo(() => excludeAttendee(attendees || [], second), [attendees, second]);
  const secondAttendees = useMemo(() => excludeAttendee(attendees || [], by), [attendees, by]);

  const byAttendeeOptions = useMemo(
    () => getAttendeeOptions(byAttendees || [], meetingSession?.presidingChairId),
    [byAttendees, meetingSession?.presidingChairId]
  );
  const secondAttendeeOptions = useMemo(
    () => getAttendeeOptions(secondAttendees || [], meetingSession?.presidingChairId),
    [secondAttendees, meetingSession?.presidingChairId]
  );

  const safeBy = useSafeAttendeeDisplayValue(by, byAttendees);
  const safeSecond = useSafeAttendeeDisplayValue(second, secondAttendees);

  const currentMotion: Motion = useMemo(() => {
    return {
      id: `incorporate-technical-change-motion`,
      title: 'Incorporation',
      statement,
      by: safeBy,
      second: safeSecond,
      discussion,
    };
  }, [discussion, safeBy, safeSecond, statement]);

  const handleClearMotion = useCallback(() => {
    setStatement(defaultStatement);
    setBy('');
    setSecond('');
    setDiscussion('');
  }, []);

  return (
    <GridContainer>
      <GridItem>
        <TextField
          label={'Motion'}
          value={statement}
          required
          onChange={(event) => {
            setStatement(event.target.value);
          }}
        ></TextField>
      </GridItem>
      <GridItem md={6}>
        <TextField
          label={'By'}
          select
          required
          value={safeBy}
          onChange={(event) => {
            setBy(event.target.value);
          }}
        >
          <MenuItem value={''}>-- None --</MenuItem>
          {byAttendeeOptions}
        </TextField>
      </GridItem>
      <GridItem md={6}>
        <TextField
          label={'Second'}
          select
          required
          value={safeSecond}
          onChange={(event) => {
            setSecond(event.target.value);
          }}
        >
          <MenuItem value={''}>-- None --</MenuItem>
          {secondAttendeeOptions}
        </TextField>
      </GridItem>
      <GridItem>
        <FormControl className="w-100">
          <FormLabel>Discussion</FormLabel>
          <Editor
            {...defaultTinyMCEProps}
            id="related-discussion-editor"
            value={discussion}
            onEditorChange={(newValue: string) => setDiscussion(newValue)}
            init={{
              ...defaultTinyMCEProps.init,
              images_upload_handler: createImagesUploadHandler(meetingId),
            }}
          />
        </FormControl>
      </GridItem>

      <GridItem>
        <MotionVotingControls
          currentMotion={currentMotion}
          onClearMotion={handleClearMotion}
          showVotingRuleSelector={false}
        />
      </GridItem>
    </GridContainer>
  );
};

export default IncorporateTechnicalChangeMotion;
